import { CommonModule } from "@angular/common";
import { Component, computed, inject, signal } from "@angular/core";
import { ActivatedRoute, RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { QuizService } from "src/app/services/api/quiz.service";
import { QuizAttempt, QuizResponseRow } from "src/app/services/model/quiz.model";
import { SharedModule } from "src/shared/shared.module";

/**
 * The result of one attempt: the score, the answer key with explanations, and
 * the per-topic breakdown.
 *
 * Instant feedback is the point — this is the mirror of the quiz screen, where
 * the key must NOT be present. The breakdown is what turns a score into
 * something a student can act on: "you got 6/10" says nothing, "you missed 3 of
 * 4 on fractions" says what to do next.
 */
@Component({
  selector: "app-quiz-result",
  imports: [SharedModule, CommonModule, RouterModule, TranslateModule],
  templateUrl: "./quiz-result.component.html",
  styleUrl: "./quiz-result.component.scss",
})
export class QuizResultComponent {
  #quizService = inject(QuizService);
  #route = inject(ActivatedRoute);

  isLoading = signal(true);
  error = signal("");
  attempt = signal<QuizAttempt | null>(null);

  responses = computed<QuizResponseRow[]>(() => this.attempt()?.quiz_responses ?? []);
  breakdown = computed(() => this.attempt()?.skill_breakdown ?? []);
  /** Accuracy split by how hard the questions were. "8 out of 10" is the same
   *  sentence whether the paper was trivial or brutal; this says which. */
  byDifficulty = computed(() => this.attempt()?.difficulty_breakdown ?? []);
  correctCount = computed(() => this.responses().filter((r) => r.is_correct).length);

  ngOnInit(): void {
    const id = Number(this.#route.snapshot.paramMap.get("attempt_id"));
    if (!id) {
      this.error.set("ไม่พบผลการทำแบบฝึกหัด");
      this.isLoading.set(false);
      return;
    }
    this.#quizService.getAttempt(id).subscribe({
      next: (attempt) => {
        this.attempt.set(attempt);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.error.set(err?.error?.errors || "ไม่พบผลการทำแบบฝึกหัด");
        this.isLoading.set(false);
      },
    });
  }

  /** What the student picked, rendered as the choice text rather than its key —
   *  "B" means nothing to a person reading their own paper back. */
  chosenText(row: QuizResponseRow): string {
    const question = row.quiz_question;
    if (!question) return row.answer ?? "";
    if (question.question_type !== "choice") return row.answer ?? "";
    const choice = (question.choices ?? []).find((c) => c.key === row.answer);
    return choice ? `${choice.key}. ${choice.text}` : "";
  }

  correctText(row: QuizResponseRow): string {
    const question = row.quiz_question;
    if (!question) return "";
    if (question.question_type !== "choice") return (question.correct_keys ?? []).join(", ");
    const correct = (question.choices ?? []).filter((c) => c.is_correct);
    return correct.map((c) => `${c.key}. ${c.text}`).join(" / ");
  }

  /**
   * Which band a percentage falls in.
   *
   * It used to return a badge class, so the only encoding of "how did I do on
   * this topic" was the colour of a pill. The row now draws a bar — length for
   * the magnitude, band for the verdict — and the number is printed beside it,
   * so nothing here is carried by colour alone.
   */
  band(percent: number): string {
    if (percent >= 80) return "is-good";
    if (percent >= 50) return "is-mid";
    return "is-low";
  }
}
